import React, { useState, useEffect } from 'react';
import { Modal, Separator, Toggle } from '@fluentui/react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { ColumnEditorModalProps, IColumnConfig } from '@/components/Instruments/Interfaces/InstrumentsInterfaces';
import * as S from '@/components/Instruments/ColumnEditorModalStyles';

const resultsOptions = [20, 50, 100, 200];

const ColumnEditorModal: React.FC<ColumnEditorModalProps> = ({isOpen, onClose, settings, onSave}) =>
{
    const [selectedResults, setSelectedResults] = useState<number>(settings.resultsPerPage);
    const [columns, setColumns] = useState<IColumnConfig[]>(settings.columns.map(c => ({...c})));

    // Sincronizar el estado local cuando se abra el modal con nuevos settings
    useEffect(() =>
    {
        if(isOpen)
        {
            setSelectedResults(settings.resultsPerPage);
            setColumns(settings.columns.map(c => ({ ...c })));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleToggleColumn = (key: string) =>
    {
        setColumns(prev => prev.map(col =>
            col.key === key && !col.locked ? { ...col, enabled: !col.enabled } : col
        ));
    };

    const handleDragEnd = (result: DropResult) =>
    {
        if(!result.destination || result.source.index === result.destination.index)
        {
            return;
        }

        const destinationIndex = result.destination.index;

        // No permitir colocar un elemento en la posición de una fila locked
        if(columns[destinationIndex]?.locked)
        {
            return;
        }

        setColumns(prev =>
        {
            const updated = [...prev];
            const [dragged] = updated.splice(result.source.index, 1);
            updated.splice(destinationIndex, 0, dragged);
            return updated;
        });
    };

    const handleSave = () =>
    {
        onSave({resultsPerPage: selectedResults, columns});
        onClose();
    };

    const handleCancel = () =>
    {
        setSelectedResults(settings.resultsPerPage);
        setColumns(settings.columns.map(c => ({...c})));
        onClose();
    };

    return(
        <Modal isOpen={isOpen} onDismiss={() => {}} isBlocking className="column-editor-modal">
            <S.ColumnEditorGlobalStyle />
            <S.ModalContainer>
                <S.Title>Editar columnas</S.Title>
                <S.Subtitle>
                    Los cambios que realices se guardarán para esta tabla.
                </S.Subtitle>
                <Separator />
                <S.ColumnsLayout>
                    <S.LeftColumn>
                        <S.SectionTitle>Resultados</S.SectionTitle>
                        <S.SectionSubtitle>Número de resultados por página:</S.SectionSubtitle>
                        <S.RadioGroup>
                            {resultsOptions.map((option) =>
                            (
                                <S.RadioItem key={option} onClick={() => setSelectedResults(option)}>
                                    <S.RadioCircle selected={selectedResults === option}>
                                        {selectedResults === option && <S.RadioDot />}
                                    </S.RadioCircle>
                                    <S.RadioLabel>{`${option} resultados`}</S.RadioLabel>
                                </S.RadioItem>
                            ))}
                        </S.RadioGroup>
                    </S.LeftColumn>
                    <S.RightColumn>
                        <S.SectionTitle>Contenido del listado</S.SectionTitle>
                        <S.SectionSubtitle>Selecciona qué columnas visualizar y su orden de presentación:</S.SectionSubtitle>
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="columns-list">
                                {(provided) =>
                                (
                                    <S.ColumnList ref={provided.innerRef} {...provided.droppableProps}>
                                        {columns.map((col, index) =>
                                        (
                                            <Draggable key={col.key} draggableId={col.key} index={index} isDragDisabled={!!col.locked}>
                                                {(provided, snapshot) =>
                                                (
                                                    <S.ColumnRow ref={provided.innerRef} {...provided.draggableProps} isDragging={snapshot.isDragging}>
                                                        <S.ColumnLabel>{col.label}</S.ColumnLabel>
                                                        <S.ColumnActions>
                                                            <Toggle checked={col.enabled} disabled={!!col.locked}
                                                                onChange={() => handleToggleColumn(col.key)}
                                                                styles={
                                                                {
                                                                    root: {marginBottom: 0},
                                                                    pill: col.locked
                                                                        ? {background: '#CC214F', border: 'none', opacity: 0.5}
                                                                        : {background: col.enabled ? '#CC214F' : '#c8c8c8', border: 'none', selectors: {':hover': {background: col.enabled ? '#a81a40' : '#b0b0b0'}}},
                                                                    thumb: {background: '#ffffff'}
                                                                }}
                                                            />
                                                            {!col.locked ?
                                                            (
                                                                <S.DragHandle {...provided.dragHandleProps}>⠿</S.DragHandle>
                                                            ) :
                                                            (
                                                                <S.DragHandleSpacer />
                                                            )}
                                                        </S.ColumnActions>
                                                    </S.ColumnRow>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </S.ColumnList>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </S.RightColumn>
                </S.ColumnsLayout>
                <S.FooterContainer>
                    <S.CancelButton onClick={handleCancel}>Cancelar</S.CancelButton>
                    <S.SaveButton onClick={handleSave}>Guardar cambios</S.SaveButton>
                </S.FooterContainer>
            </S.ModalContainer>
        </Modal>
    );
};

export default ColumnEditorModal;
